import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { CheckCircleIcon, AlertIcon, InfoIcon, XIcon } from '../icons';
import { Button, IconButton } from './Button';
import { S } from '../strings';
import './Toast.css';

export type ToastTone = 'neutral' | 'ok' | 'warning' | 'danger';

export interface ToastOptions {
  tone?: ToastTone;
  /** Auto-dismiss in ms; 0 keeps it until dismissed. Default 5000 (8000 with an action). */
  duration?: number;
  action?: { label: string; onClick: () => void };
  /** Replace an existing toast with the same key instead of stacking. */
  key?: string;
}

export interface ProgressHandle {
  /** Update "Läser kvitton 2 av 3…" style text and optional fraction. */
  update(message: string, done?: number, total?: number): void;
  /** Finish with a final message (auto-dismisses) or silently. */
  finish(message?: string, tone?: ToastTone): void;
}

export interface ToastApi {
  show(message: string, opts?: ToastOptions): string;
  dismiss(id: string): void;
  progress(message: string, done?: number, total?: number): ProgressHandle;
}

interface ToastRecord {
  id: string;
  message: string;
  tone: ToastTone;
  duration: number;
  action?: { label: string; onClick: () => void };
  progress?: { done?: number | undefined; total?: number | undefined } | undefined;
  key?: string | undefined;
}

const ToastContext = createContext<ToastApi | null>(null);

let seq = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastRecord[]>([]);
  const timers = useRef(new Map<string, number>());
  /** key → id, so a keyed toast replaces its predecessor instead of stacking. */
  const keyIds = useRef(new Map<string, string>());

  const dismiss = useCallback((id: string) => {
    setToasts((list) => list.filter((t) => t.id !== id));
    const timer = timers.current.get(id);
    if (timer) {
      window.clearTimeout(timer);
      timers.current.delete(id);
    }
    for (const [k, v] of keyIds.current) if (v === id) keyIds.current.delete(k);
  }, []);

  const arm = useCallback(
    (id: string, duration: number) => {
      const existing = timers.current.get(id);
      if (existing) window.clearTimeout(existing);
      if (duration > 0) timers.current.set(id, window.setTimeout(() => dismiss(id), duration));
    },
    [dismiss],
  );

  const upsert = useCallback((record: ToastRecord) => {
    setToasts((list) => {
      const idx = list.findIndex((t) => t.id === record.id);
      if (idx >= 0) {
        const next = [...list];
        next[idx] = record;
        return next;
      }
      return [...list.slice(-3), record];
    });
  }, []);

  const show = useCallback(
    (message: string, opts: ToastOptions = {}) => {
      const id = (opts.key && keyIds.current.get(opts.key)) || `toast-${++seq}`;
      if (opts.key) keyIds.current.set(opts.key, id);
      const duration = opts.duration ?? (opts.action ? 8000 : 5000);
      const record: ToastRecord = { id, message, tone: opts.tone ?? 'neutral', duration, key: opts.key };
      if (opts.action) record.action = opts.action;
      upsert(record);
      arm(id, duration);
      return id;
    },
    [arm, upsert],
  );

  const progress = useCallback(
    (message: string, done?: number, total?: number): ProgressHandle => {
      const id = `toast-${++seq}`;
      const base: ToastRecord = { id, message, tone: 'neutral', duration: 0, progress: { done, total } };
      upsert(base);
      return {
        update(msg, d, t) {
          setToasts((list) => list.map((x) => (x.id === id ? { ...x, message: msg, progress: { done: d, total: t } } : x)));
        },
        finish(msg, tone = 'ok') {
          if (!msg) {
            dismiss(id);
            return;
          }
          setToasts((list) => list.map((x) => (x.id === id ? { id, message: msg, tone, duration: 4000 } : x)));
          arm(id, 4000);
        },
      };
    },
    [arm, dismiss, upsert],
  );

  useEffect(() => {
    const map = timers.current;
    return () => {
      for (const t of map.values()) window.clearTimeout(t);
    };
  }, []);

  const api = useMemo<ToastApi>(() => ({ show, dismiss, progress }), [show, dismiss, progress]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}

function ToneIcon({ tone }: { tone: ToastTone }) {
  switch (tone) {
    case 'ok':
      return <CheckCircleIcon size={20} className="toast-icon toast-icon-ok" />;
    case 'warning':
      return <AlertIcon size={20} className="toast-icon toast-icon-warning" />;
    case 'danger':
      return <AlertIcon size={20} className="toast-icon toast-icon-danger" />;
    case 'neutral':
      return <InfoIcon size={20} className="toast-icon" />;
    default: {
      const exhaustive: never = tone;
      throw new Error(`Unknown tone ${String(exhaustive)}`);
    }
  }
}

function ToastViewport({ toasts, onDismiss }: { toasts: ToastRecord[]; onDismiss: (id: string) => void }) {
  return (
    <div className="toast-viewport" aria-live="polite" aria-relevant="additions text">
      {toasts.map((t) => (
        <div key={t.id} className={`toast toast-${t.tone}`} role="status">
          {t.progress ? <ProgressRing done={t.progress.done} total={t.progress.total} /> : <ToneIcon tone={t.tone} />}
          <span className="toast-message">{t.message}</span>
          {t.action ? (
            <Button
              size="s"
              variant="secondary"
              className="toast-action"
              onClick={() => {
                t.action?.onClick();
                onDismiss(t.id);
              }}
            >
              {t.action.label}
            </Button>
          ) : null}
          {!t.progress ? <IconButton icon={XIcon} size="s" aria-label={S.close} tooltip={false} onClick={() => onDismiss(t.id)} /> : null}
        </div>
      ))}
    </div>
  );
}

function ProgressRing({ done, total }: { done?: number | undefined; total?: number | undefined }) {
  const fraction = done !== undefined && total ? Math.min(1, done / total) : null;
  const r = 8;
  const c = 2 * Math.PI * r;
  return (
    <svg viewBox="0 0 20 20" width={20} height={20} className={fraction === null ? 'toast-ring toast-ring-spin' : 'toast-ring'} aria-hidden="true">
      <circle cx="10" cy="10" r={r} fill="none" stroke="var(--line-strong)" strokeWidth="2" />
      <circle
        cx="10"
        cy="10"
        r={r}
        fill="none"
        stroke="var(--brand)"
        strokeWidth="2"
        strokeLinecap="round"
        strokeDasharray={c}
        strokeDashoffset={fraction === null ? c * 0.7 : c * (1 - fraction)}
        transform="rotate(-90 10 10)"
      />
    </svg>
  );
}

/**
 * Convenience wrapper for a long-running background task: shows a progress
 * toast while `running`, updates the message, and finishes with a summary.
 */
export function ProgressToast({ running, message, done, total, finishedMessage }: { running: boolean; message: string; done?: number; total?: number; finishedMessage?: string }) {
  const toast = useToast();
  const handle = useRef<ProgressHandle | null>(null);
  useEffect(() => {
    if (running && !handle.current) handle.current = toast.progress(message, done, total);
    else if (running && handle.current) handle.current.update(message, done, total);
    else if (!running && handle.current) {
      handle.current.finish(finishedMessage);
      handle.current = null;
    }
  }, [running, message, done, total, finishedMessage, toast]);
  useEffect(() => () => handle.current?.finish(), []);
  return null;
}

/** Thin inline progress line with counts, used above lists while the AI reads. */
export function ProgressLine({ label, done, total }: { label: string; done?: number; total?: number }) {
  const fraction = done !== undefined && total ? Math.min(1, done / total) : null;
  return (
    <div className="progress-line" role="progressbar" aria-label={label} aria-valuemin={0} aria-valuemax={total ?? undefined} aria-valuenow={done ?? undefined}>
      <div className="progress-line-text">
        <span>{label}</span>
        {done !== undefined && total ? (
          <span className="amount">
            {done} av {total}
          </span>
        ) : null}
      </div>
      <div className="progress-line-track">
        <div className={fraction === null ? 'progress-line-bar progress-line-indeterminate' : 'progress-line-bar'} style={fraction === null ? undefined : { width: `${fraction * 100}%` }} />
      </div>
    </div>
  );
}
